import { jsx as _jsx } from "react/jsx-runtime";
import Notification20 from '@carbon/icons-react/es/notification/20';
import { Suspense, lazy, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CompactMenuTitle } from 'components/atoms/menu/compact-menu';
import { DrawerContent } from 'components/organisms/task-pane/task-detail/header/add-time-entry/drawer-content';
import { RoutePaths } from 'constant';
import { useNotificationsDrawerContext } from 'contexts/providers/notifications-drawer';
import { usePending } from 'stores/notifications';
import { handleLastSeenAtSubscription } from 'stores/notifications/use-pending';
import { useNotificationsDrawer } from './hash-based-notifications';
import { Drawer } from './styled';
/**
 * Cached chunk-load promise. The drawer title is rendered atomically
 * by the Drawer component, but the body (this List) is React.lazy
 * wrapped in <Suspense fallback={null}>. If the user clicks the bell
 * before the chunk arrives, the body briefly renders as nothing —
 * empty drawer with just a title — and downstream code (e.g. the
 * filter pills locator in TC-1115) sees an empty DOM at click time.
 *
 * Expose a prefetch helper so the left-nav can warm the chunk up at
 * bell-button mount (idle-callback gated). The lazy() still wraps
 * the same promise, so the code-split chunk shape is unchanged.
 */ let listChunkPromise;
export const prefetchNotificationsList = ()=>{
    if (!listChunkPromise) {
        listChunkPromise = import('../list');
    }
    return listChunkPromise;
};
const List = /*#__PURE__*/ lazy(()=>prefetchNotificationsList().then((m)=>({
            default: m.List
        })));
const StyledTitle = styled.span`
  font-size: var(--heading-5-size);
`;
export const NotificationsDrawer = ()=>{
    const [params, , hide] = useNotificationsDrawer();
    const { visibility, setVisibility } = useNotificationsDrawerContext();
    const [pending, resetPending] = usePending();
    const refreshRef = useRef();
    const listRef = useCallback((instance)=>{
        if ((instance === null || instance === void 0 ? void 0 : instance.refresh) != null) {
            refreshRef.current = instance.refresh;
        }
    }, []);
    useEffect(()=>{
        handleLastSeenAtSubscription();
        return ()=>{
            handleLastSeenAtSubscription().unsubscribe();
        };
    }, []);
    const onClose = useCallback(()=>{
        setVisibility(false);
        setTimeout(hide, 100);
    }, [
        hide,
        setVisibility
    ]);
    useEffect(()=>{
        switch(params[0]){
            case 'show':
                setVisibility(true);
                if (pending > 0) {
                    resetPending();
                }
                break;
            case 'invalid':
            default:
                setVisibility(false);
        }
    }, [
        params,
        pending,
        resetPending,
        setVisibility
    ]);
    return /*#__PURE__*/ _jsx(Drawer, {
        onClose: onClose,
        destroyOnClose: true,
        open: visibility,
        title: /*#__PURE__*/ _jsx(CompactMenuTitle, {
            icon: /*#__PURE__*/ _jsx(Notification20, {}),
            text: /*#__PURE__*/ _jsx(StyledTitle, {
                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                    id: "notifications"
                })
            })
        }),
        children: /*#__PURE__*/ _jsx(DrawerContent.Content, {
            children: /*#__PURE__*/ _jsx(Suspense, {
                fallback: null,
                children: /*#__PURE__*/ _jsx(List, {
                    ref: listRef,
                    pending: pending,
                    configScreenUrl: RoutePaths.notificationsSettings,
                    resetPending: resetPending
                })
            })
        })
    });
};
